<template>
  <v-container id="grn-items" class="custom-container">
    <!-- Error Alert -->
    <v-alert
      v-if="showError"
      type="error"
      dismissible
      @input="showError = false"
    >
      Received quantity cannot exceed ordered quantity!
    </v-alert>

    <v-data-table
      v-if="!loadingBatches"
      :headers="headers"
      :items="grnItems"
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style="border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="{ item, index }">
        <tr :key="index">
          <td>
            <v-autocomplete
              v-model="item.product_code"
              required
              dense
              outlined
              clearable
              :items="products"
              item-text="product_code"
              item-value="product_code"
              @change="onProductSelect(item)"
            ></v-autocomplete>
          </td>

          <td>
            <v-autocomplete
              v-model="item.batch_id"
              required
              dense
              outlined
              clearable
              :items="getAvailableBatches(item)"
              item-text="batch_code"
              item-value="id"
              @input="onBatchSelect(item)"
            ></v-autocomplete>
          </td>
          <td class="wider-description">
            <v-text-field
              v-model="item.description"
              placeholder="Description"
              outlined
              dense
              clearable
            ></v-text-field>
          </td>

          <td>
            <v-text-field
              v-model="item.quantity"
              type="number"
              dense
              min="0"
              outlined
              readonly
              @input="updateValues(item)"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.quantity_already_received"
              type="number"
              dense
              min="0"
              outlined
              readonly
              @input="updateValues(item)"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.quantity_received"
              type="number"
              dense
              min="0"
              :max="quantityOrdered"
              outlined
              clearable
              @input="checkQuantityReceived(item)"
            ></v-text-field>
          </td>

          <td class="pb-5">
            <v-icon size="30" @click="deleteItem(index)" color="red">
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>
    <!-- <v-row class="mt-0">
      <v-col v-if="!addingNewRow" class="text-left">
        <v-icon size="40" color="blue" @click="addNewRow">mdi-plus</v-icon>
      </v-col>
    </v-row> -->
    <v-dialog v-model="showBatchModal" width="800px">
      <v-card>
        <v-row>
          <v-col class="d-flex justify-end" cols="12">
            <v-btn icon @click="onBatchCreateClose">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <BatchCreateForm
          :productId="this.currentProductId"
          @close="onBatchCreateClose"
          @batchCreated="onBatchCreated"
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { GrnService } from "@/services/grn-service.js";
import BatchCreateForm from "@/components/batches/BatchCreateForm.vue";
export default {
  props: {
    InitialGrnItems: Array,
    products: { type: Array, required: true },
  },
  components: {
    BatchCreateForm,
  },
  data() {
    return {
      headers: [
        { text: "Item Code", value: "product_code" },
        { text: "Batch ", value: "batch_id" },
        { text: "Description", value: "description" },
        { text: "Quantity Ordered", value: "quantity" },
        { text: "Quantity Already Received", value: "quantity" },
        { text: "Quantity Received", value: "quantity_received" },

        { text: "", value: "actions", sortable: false },
      ],
      grnItems: [],
      addingNewRow: false,
      totalAmount: 0,
      ordered: 0,
      batches: {},
      showError: false,
      showBatchModal: false,
      currentProductId: null,
      loadingBatches: false,
    };
  },

  watch: {
    products: {
      immediate: true,
      async handler(newProducts) {
        if (newProducts && newProducts.length > 0) {
          this.grnItems = newProducts.map((product) => ({
            po_item_id: product.id,
            product_id: product.product_id,
            product_code: product.product_code,
            batch_id: null,
            description: product.description || "",
            quantity: product.quantity || 0,
            quantity_already_received: product.quantity_already_received || 0,
            quantity_received: 0,
            unit_price: product.unit_price || 0,
            discount_percent: product.discount_percent || 0,
            discount_amount: 0,
            total_price: 0,
            tax_percent: product.tax_percent || 0,
          }));
          await this.fetchAllBatches(newProducts);
        } else {
          this.grnItems = [];
        }
      },
    },
    InitialGrnItems: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.grnItems = [...newVal];
        } else {
          this.grnItems.push(this.getDefaultRow());
        }
      },
    },
    grnItems: {
      deep: true,
      handler(newValue) {
        const total = this.calculateTotalAmount();
        this.$emit("total", total);
        this.$emit("items", newValue);
      },
    },
  },
  methods: {
    checkQuantityReceived(item) {
      if (
        item.quantity_received >
        item.quantity - item.quantity_already_received
      ) {
        this.showError = true;
        item.quantity_received = item.quantity - item.quantity_already_received;
      } else {
        this.showError = false;
      }
      this.updateValues(item);
    },

    async fetchAllBatches(products) {
      this.loadingBatches = true;
      this.batches = {};
      for (const product of products) {
        try {
          const batchesRes = await GrnService.getBatchList(product.product_id);
          if (batchesRes.data && batchesRes.data.data) {
            this.batches[product.product_id] = batchesRes.data.data;
          }
        } catch (err) {
          console.error(
            `Error fetching batches for product ${product.id}:`,
            err
          );
        }
      }
      this.loadingBatches = false;
    },

    getAvailableBatches(item) {
      const productId = item.product_id;
      if (!productId) return [{ id: "create", batch_code: "Create New Batch" }];
      const selectedBatchIds = this.grnItems
        .filter((i) => i !== item && i.product_id === productId && i.batch_id)
        .map((i) => i.batch_id);

      const batchesForProduct = this.batches[productId] || [];
      return [
        { id: "create", batch_code: "Create New Batch" },
        ...batchesForProduct.filter(
          (batch) =>
            !selectedBatchIds.includes(batch.id) || batch.id === item.batch_id
        ),
      ];
    },

    onProductSelect(item) {
      const selectedProduct = this.products.find(
        (product) => product.product_code === item.product_code
      );
      this.currentProductId = selectedProduct ? selectedProduct.id : null;
      if (selectedProduct) {
        item.product_id = selectedProduct.id;
        item.quantity = selectedProduct.quantity;
        item.quantity_already_received =
          selectedProduct.quantity_already_received || 0;
        item.unit_price = selectedProduct.unit_price;
        item.discount_percent = selectedProduct.discount_percent || 0;
        item.tax_percent = selectedProduct.tax_percent || 0;

        this.fetchBatchesForProduct(selectedProduct.id);

        this.updateValues(item);
      }
    },
    onBatchSelect(item) {
      if (item.batch_id === "create") {
        this.showBatchModal = true;
      } else {
        const duplicate = this.grnItems.find(
          (grnItem) =>
            grnItem !== item &&
            grnItem.product_code === item.product_code &&
            grnItem.batch_id === item.batch_id
        );

        if (duplicate) {
          item.batch_id = null;
        } else {
          this.showError = false;
          const selectedBatch = this.batches.find(
            (batch) => batch.id === item.batch_id
          );
          if (selectedBatch) {
            console.log("Selected batch:", selectedBatch);
          }
        }
      }
    },
    async fetchBatchesForProduct(productId) {
      try {
        this.loadingBatches = true;
        const batchesRes = await GrnService.getBatchList(productId);
        if (batchesRes.data && batchesRes.data.data) {
          this.$set(this.batches, productId, batchesRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching batches:", err);
      } finally {
        this.loadingBatches = false;
      }
    },
    onBatchCreated() {
      this.showBatchModal = false;
      if (this.currentProductId) {
        this.fetchBatchesForProduct(this.currentProductId);
      }
    },
    onBatchCreateClose() {
      this.showBatchModal = false;
      this.grnItems.forEach((item) => {
        if (item.batch_id === "create") {
          item.batch_id = null;
        }
      });
    },
    updateValues(item) {
      const baseAmount = item.quantity_received * item.unit_price;
      const discountAmount = (baseAmount * item.discount_percent) / 100;
      const finalAmount = baseAmount - discountAmount;

      item.total_price = (finalAmount * (1 + 10 / 100)).toFixed(2);
      item.tax_amount = (finalAmount * (item.tax_percent / 100)).toFixed(2);
      item.discount_amount = discountAmount.toFixed(2);
    },
    deleteItem(index) {
      this.grnItems.splice(index, 1);
    },
    addNewRow() {
      this.addingNewRow = true;
      this.$nextTick(() => {
        this.grnItems.push(this.getDefaultRow());
        this.addingNewRow = false;
      });
    },
    getDefaultRow() {
      return {
        description: "",
        quantity_received: 0,
        unit_price: 0,
        discount_amount: 0,
        discount_percent: 0,
        total_price: 0,
        tax_percent: 0,
      };
    },
    calculateTotalAmount() {
      this.totalAmount = 0;
      this.grnItems.forEach((item) => {
        this.totalAmount += parseFloat(item.total_price);
      });
      return this.totalAmount;
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}
.custom-container {
  width: 100%; /* Make container stretch to full width */
  max-width: 100%; /* Ensure container uses the full available space */
  padding: 0 !important; /* Remove padding or set a custom value */
  margin: 0 !important; /* Remove margins or set a custom value */
}
.v-data-table td {
  padding: 1px !important; /* Adjust padding to reduce extra space */
  margin: 0 !important;
  text-align: center; /* Optional: Align text in the middle */
  width: 10%; /* Make each <td> take equal space (100% / 8 columns = 12.5%) */
}
.v-data-table th {
  padding: 1px !important;
  text-align: center; /* Optional: Align text in the middle */
}

.v-data-table {
  table-layout: fixed; /* Forces table to distribute space evenly across columns */
}
</style>
